import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import "./DealAssistant.css";
import { FaRobot, FaTimes, FaPaperPlane, FaMinus } from "react-icons/fa";
import api from "../api/axios";
import { useAuth } from "../auth/AuthContext";

// How long to wait for the streaming response before aborting (item #27).
const STREAM_TIMEOUT_MS = 45000;


const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
let recognition;

if (SpeechRecognition) {
  recognition = new SpeechRecognition();
  recognition.continuous = false;
  recognition.interimResults = false;
  // Auto-detect browser language, fallback to en-US for speech recognition
  const browserLang = (navigator.language || navigator.userLanguage || 'en-US');
  // Use full locale code for better language support (e.g., en-US, es-ES, fr-FR, de-DE)
  const supportedLangs = ['en-US', 'en-GB', 'es-ES', 'es-MX', 'fr-FR', 'de-DE', 'it-IT', 'ja-JP', 'zh-CN', 'pt-BR'];
  recognition.lang = supportedLangs.includes(browserLang) ? browserLang : 
                      supportedLangs.some(l => l.startsWith(browserLang.split('-')[0])) 
                        ? supportedLangs.find(l => l.startsWith(browserLang.split('-')[0]))
                        : 'en-US';
}


const DealAssistant = () => {
  const PANEL_WIDTH = 360;
  const PANEL_HEIGHT = 520;
  const ICON_SIZE = 56;
  const VIEWPORT_GAP = 8;

  const getInitialPosition = () => {
    if (typeof window === "undefined") return { x: 24, y: 24 };
    
    // Try to load from localStorage first
    try {
      const saved = localStorage.getItem("dealAssistantPosition");
      if (saved) {
        const parsed = JSON.parse(saved);
        return { x: parsed.x || 0, y: parsed.y || 0 };
      }
    } catch (err) {
      console.warn("Failed to load saved position:", err);
    }
    
    // Fallback to default position
    return {
      x: Math.max(VIEWPORT_GAP, window.innerWidth - ICON_SIZE - 28),
      y: Math.max(VIEWPORT_GAP, window.innerHeight - ICON_SIZE - 28),
    };
  };

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [dealState, setDealState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(getInitialPosition);
  const [panelPosition, setPanelPosition] = useState({ vertical: "bottom", horizontal: "left" });

  const booted = useRef(false);
  const dragStateRef = useRef({
    isDragging: false,
    startPointerX: 0,
    startPointerY: 0,
    startX: 0,
    startY: 0,
    moved: false,
  });

  const getBounds = useCallback((panelOpen) => {
    if (typeof window === "undefined") {
      return { minX: VIEWPORT_GAP, maxX: VIEWPORT_GAP, minY: VIEWPORT_GAP, maxY: VIEWPORT_GAP };
    }

    const maxX = Math.max(VIEWPORT_GAP, window.innerWidth - (panelOpen ? PANEL_WIDTH : ICON_SIZE) - VIEWPORT_GAP);
    const maxY = Math.max(VIEWPORT_GAP, window.innerHeight - ICON_SIZE - VIEWPORT_GAP);
    const minY = panelOpen ? Math.min(PANEL_HEIGHT + 16, maxY) : VIEWPORT_GAP;

    return {
      minX: VIEWPORT_GAP,
      maxX,
      minY,
      maxY,
    };
  }, []);

  const clampPosition = useCallback((x, y, panelOpen) => {
    const bounds = getBounds(panelOpen);
    return {
      x: Math.min(bounds.maxX, Math.max(bounds.minX, x)),
      y: Math.min(bounds.maxY, Math.max(bounds.minY, y)),
    };
  }, [getBounds]);

  const calculatePanelPosition = useCallback((iconX, iconY) => {
    if (typeof window === "undefined") return { vertical: "top", horizontal: "right" };

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // "top" class = panel opens BELOW the icon (top edge anchored)
    // "bottom" class = panel opens ABOVE the icon (bottom edge anchored)
    // So: icon in top half → more space below → open downward → use "top"
    //     icon in bottom half → more space above → open upward → use "bottom"
    const spaceBelow = viewportHeight - iconY - ICON_SIZE;
    const spaceAbove = iconY;
    const vertical = spaceBelow >= spaceAbove ? "top" : "bottom";

    // Determine horizontal: prefer side with more space for the panel
    const spaceRight = viewportWidth - iconX - ICON_SIZE;
    const spaceLeft = iconX;
    const horizontal = spaceRight >= spaceLeft ? "right" : "left";

    return { vertical, horizontal };
  }, []);

  const startDrag = useCallback((event) => {
    const clientX = event.clientX ?? (event.touches && event.touches[0]?.clientX);
    const clientY = event.clientY ?? (event.touches && event.touches[0]?.clientY);
    if (clientX === undefined || clientY === undefined) return;

    dragStateRef.current = {
      isDragging: true,
      startPointerX: clientX,
      startPointerY: clientY,
      startX: position.x,
      startY: position.y,
      moved: false,
    };
  }, [position.x, position.y]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!dragStateRef.current.isDragging) return;

      const clientX = event.clientX;
      const clientY = event.clientY;
      const dx = clientX - dragStateRef.current.startPointerX;
      const dy = clientY - dragStateRef.current.startPointerY;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        dragStateRef.current.moved = true;
      }

      const next = clampPosition(
        dragStateRef.current.startX + dx,
        dragStateRef.current.startY + dy,
        open
      );
      setPosition(next);
    };

    const handlePointerUp = () => {
      dragStateRef.current.isDragging = false;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [clampPosition, open]);

  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev.x, prev.y, open));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [clampPosition, open]);

  // Persist position to localStorage
  useEffect(() => {
    try {
      localStorage.setItem("dealAssistantPosition", JSON.stringify(position));
    } catch (err) {
      console.warn("Failed to save position:", err);
    }
  }, [position]);

  const baseUrl = api.defaults.baseURL || "";
  const token = localStorage.getItem("access_token");
  const { logout } = useAuth();

  const authHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    }),
    [token]
  );

  const appendAiMessage = (text) => {
    if (!text) return;
    setMessages((prev) => [...prev, { sender: "ai", text }]);
  };

  const callDealChat = useCallback(
    async (userInput, options = {}) => {
      const trimmed = userInput.trim();
      if (!trimmed) return;

      if (!options.isBoot) {
        setMessages((prev) => [...prev, { sender: "user", text: trimmed }]);
      }
      setLoading(true);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), STREAM_TIMEOUT_MS);

      try {
        const res = await fetch(`${baseUrl}/api/deal-chat/stream`, {
          method: "POST",
          headers: authHeaders,
          body: JSON.stringify({
            message: trimmed,
            deal_state: dealState,
          }),
          signal: controller.signal,
        });

        if (res.status === 401) {
          // Session expired mid-chat (item #26). We don't have a token-refresh
          // endpoint in this app's auth flow, so surface a clear message and
          // sign the user out so they're routed back to login instead of
          // silently failing on every subsequent message.
          setMessages((prev) => [
            ...prev,
            {
              sender: "ai",
              text: "Your session has expired. Please sign in again to continue chatting.",
            },
          ]);
          logout();
          return;
        }

        if (!res.ok || !res.body) {
          throw new Error("Streaming not supported");
        }

        let aiText = "";
        let aiMessageIndex = null;
        if (options.isBoot) {
          setMessages([{ sender: "ai", text: "" }]);
          aiMessageIndex = 0;
        } else {
          setMessages((prev) => {
            aiMessageIndex = prev.length;
            return [...prev, { sender: "ai", text: "" }];
          });
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let shouldStop = false;

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split("\n\n");
          buffer = events.pop() || "";

          for (const event of events) {
            const line = event.split("\n").find((l) => l.startsWith("data: "));
            if (!line) continue;
            const payload = JSON.parse(line.replace("data: ", ""));
            if (payload.done) {
              if (payload.deal_state) setDealState(payload.deal_state);
              shouldStop = true;
              break;
            }
            if (payload.token) {
              aiText += payload.token;
              const nextText = aiText;
              const capturedIndex = aiMessageIndex;
              setMessages((prev) => {
                if (capturedIndex === null || !prev[capturedIndex]) return prev;
                const updated = [...prev];
                updated[capturedIndex] = { ...updated[capturedIndex], text: nextText };
                return updated;
              });
            }
          }
          if (shouldStop) {
            await reader.cancel();
            break;
          }
        }
      } catch (error) {
        console.error("Chat error:", error);
        const errorMsg = error.name === "AbortError"
          ? "The assistant took too long to respond. Please try again."
          : error.message === "Streaming not supported"
          ? "Streaming mode unavailable. Please check your connection and try again."
          : "Sorry, something went wrong talking to the assistant. Please try again.";
        
        setMessages((prev) => {
          if (options.isBoot) {
            return [
              {
                sender: "ai",
                text: "Hi! I am your CRM Assistant. Ask me about deals, companies, contacts, or anything in your pipeline.",
              },
            ];
          }
          return [
            ...prev,
            {
              sender: "ai",
              text: errorMsg,
            },
          ];
        });
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
      }
    },
    [authHeaders, baseUrl, dealState, logout]
  );



  // Boot greeting: fires only once when the panel is first opened
  useEffect(() => {
    if (open && !booted.current) {
      booted.current = true;
      callDealChat("Hello, briefly introduce yourself and what you can help with.", { isBoot: true });
    }
  }, [open, callDealChat]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    callDealChat(input);
    setInput("");
  };



  const messagesEndRef = useRef(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleToggleFromButton = () => {
    if (dragStateRef.current.moved) {
      dragStateRef.current.moved = false;
      return;
    }
    setOpen((prev) => {
      if (!prev) {
        // Opening: calculate panel position based on icon position
        const panelPos = calculatePanelPosition(position.x, position.y);
        setPanelPosition(panelPos);
      }
      return !prev;
    });
  };

  const handleMinimize = () => {
    setOpen(false);
  };

  const handleCloseChat = () => {
    setOpen(false);
    setMessages([]);
    setInput("");
    setDealState(null);
    booted.current = false;
  };

  return (
    <div
      className="deal-assistant-drag-layer"
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
    >
      {/* Floating AI Assistant Button */}
      <button
        className="deal-assistant-float-btn"
        onPointerDown={startDrag}
        onClick={handleToggleFromButton}
        aria-label="Toggle AI Assistant"
        title="AI CRM Assistant"
      >
        <FaRobot size={26} />
      </button>

      {/* Side Panel */}
      {open && (
        <div className={`deal-assistant-panel panel-${panelPosition.vertical}-${panelPosition.horizontal}`}>
          <div className="deal-assistant-header" onPointerDown={startDrag}>
            <span className="deal-assistant-header-title">
              <FaRobot style={{ marginRight: 8 }} /> Deal Manager Assistant
            </span>
            <div className="deal-assistant-header-actions">
              <button
                className="deal-assistant-minimize-btn"
                onClick={handleMinimize}
                aria-label="Minimize"
                title="Minimize"
              >
                <FaMinus />
              </button>
              <button
                className="deal-assistant-close-btn"
                onClick={handleCloseChat}
                aria-label="Close"
                title="Close and reset chat"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="chat-window">
            {messages.map((msg, idx) => (
              <div key={idx} className={`message ${msg.sender}`}>
                {msg.text}
              </div>
            ))}
            {loading && (
              <div className="message ai typing-indicator">
                <span /><span /><span />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>



          <form onSubmit={handleSubmit} className="chat-input">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask me anything about your deals..."
              disabled={loading}
              autoFocus
            />
            <button type="submit" disabled={loading || !input.trim()} className="send-btn">
              <FaPaperPlane />
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default DealAssistant;
